import type { PaymentRecord, PaymentSplitResult } from "@lumora/bridge";
import { formatUsd } from "@lumora/core";
import { useState } from "react";
import { usePlatformPaymentsVm } from "./platformViewModels.js";
import { ConfirmGate } from "./ConfirmGate.js";
import { splitEligiblePayments, splitErrorText, splitResultLines } from "./paymentSplitView.js";

export type SplitOutcome = { ok: boolean; lines: string[] };

const wrap = { minWidth: 0, overflowWrap: "anywhere" as const };

type ViewProps = {
  payments: PaymentRecord[];
  outcomes: Record<string, SplitOutcome>;
  onRun: (paymentId: string) => void;
};

/** Presentational part (static-renderable). Hidden when nothing is eligible. */
export function PaymentSplitView({ payments, outcomes, onRun }: ViewProps) {
  const eligible = splitEligiblePayments(payments);
  if (eligible.length === 0) return null;
  return (
    <section aria-label="Partner split" className="platform-split-section">
      <h3>Partner split</h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {eligible.map((p) => {
          const o = outcomes[p.id];
          return (
            <li key={p.id} style={{ ...wrap, margin: "8px 0" }}>
              <span style={wrap}>
                {formatUsd(p.amountCents)}
                {p.stripeInvoiceId ? ` · ${p.stripeInvoiceId}` : ""}
              </span>{" "}
              <ConfirmGate
                idleLabel="Send partner shares"
                confirmLabel="Yes, run split"
                onConfirm={() => onRun(p.id)}
              />
              {o ? (
                <div
                  className={
                    o.ok ? "muted platform-provision-status" : "ops-error platform-provision-status"
                  }
                  role="status"
                  style={wrap}
                >
                  {o.lines.map((l, i) => (
                    <div key={i} style={wrap}>
                      {l}
                    </div>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function PaymentSplitSection({ slug, payments }: { slug: string; payments: PaymentRecord[] }) {
  const [outcomes, setOutcomes] = useState<Record<string, SplitOutcome>>({});
  const vm = usePlatformPaymentsVm();
  const run = (id: string) => {
    setOutcomes((m) => ({ ...m, [id]: { ok: true, lines: ["Sending partner shares…"] } }));
    vm
      .runSplit(slug, id)
      .then((r: PaymentSplitResult) => {
        const ok = !r.lines.some((l) => l.status === "failed");
        setOutcomes((m) => ({ ...m, [id]: { ok, lines: splitResultLines(r) } }));
      })
      .catch((err: unknown) =>
        setOutcomes((m) => ({ ...m, [id]: { ok: false, lines: [splitErrorText(err)] } })),
      );
  };
  return <PaymentSplitView payments={payments} outcomes={outcomes} onRun={run} />;
}
